/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { formFields } from './form-fields';
import { omit } from 'lodash';

/**
 * `PharmacyPlanType` type.
 */

type PharmacyPlanType = {
  id: string;
  name: string;
  campaignCostPerDay: number | null;
  maxConcurrentCampaigns: number | null;
  maxCampaignsPerMonth: number | null;
  freeMonthlyCampaigns: number | null;
  freeTokenDurationDays: number | null;
  hasSubscription: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyPlanType> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: item => (item.isDefault ? `${item.name} (Default)` : item.name)
  },
  {
    title: translate('common.table.columns.planHasSubscription'),
    dataIndex: 'hasSubscription',
    key: 'hasSubscription',
    size: '120px',
    render: (value: any) => {
      return value?.hasSubscription ? '✅' : '❌';
    }
  },
  {
    title: translate('common.table.columns.campaignCostPerDay'),
    dataIndex: 'campaignCostPerDay',
    key: 'campaignCostPerDay',
    size: '130px',
    render: item => item.campaignCostPerDay ?? '—'
  },
  {
    title: translate('common.table.columns.maxConcurrentCampaigns'),
    dataIndex: 'maxConcurrentCampaigns',
    key: 'maxConcurrentCampaigns',
    size: '130px',
    render: item => item.maxConcurrentCampaigns ?? translate('common.labels.unlimited')
  },
  {
    title: translate('common.table.columns.maxCampaignsPerMonth'),
    dataIndex: 'maxCampaignsPerMonth',
    key: 'maxCampaignsPerMonth',
    size: '120px',
    render: item => item.maxCampaignsPerMonth ?? translate('common.labels.unlimited')
  },
  {
    title: translate('common.table.columns.freeMonthlyCampaigns'),
    dataIndex: 'freeMonthlyCampaigns',
    key: 'freeMonthlyCampaigns',
    size: '120px',
    render: item => item.freeMonthlyCampaigns ?? '—'
  },
  {
    title: translate('common.table.columns.freeTokenDurationDays'),
    dataIndex: 'freeTokenDurationDays',
    key: 'freeTokenDurationDays',
    size: '130px',
    render: item => item.freeTokenDurationDays ?? '—'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '160px',
    render: item => <DateTime date={item.createdAt} />
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyPlanType> {
  return {
    columns: listColumns(translate),
    route: appRoutes.dashboard.services.pharmacyPlans,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: false,
    key: ['pharmacyPlanTypes'],
    endpoint: apiEndpoints.pharmacyPlan.planTypes as any,
    endpointById: apiEndpoints.pharmacyPlan.planTypesById as any
  };
}

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<PharmacyPlanType> => ({
  formFields: formFields(translate),
  normalizeInitialValues: () => ({
    freeTokenDurationDays: 31,
    campaignCostPerDay: 50,
    maxConcurrentCampaigns: 0,
    maxCampaignsPerMonth: 0
  }),
  normalizePayload: (values: any) => ({
    ...values,
    hasSubscription: values.hasSubscription ?? false
  })
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<PharmacyPlanType> => ({
  formFields: formFields(translate),
  submitLabel: translate('common.actions.edit'),
  endpoint: apiEndpoints.pharmacyPlan.planTypesById,
  redirect: appRoutes.dashboard.services.pharmacyPlans,
  normalizePayload: (values: any) => omit(values, ['id', 'createdAt', 'updatedAt'])
});

/**
 * Export `createPharmacyPlanTypesTemplate`.
 */

export function createPharmacyPlanTypesTemplate(translate: TFunction): CrudTemplate<PharmacyPlanType> {
  return normalizeTemplate(translate, {
    list: list(translate),
    add: add(translate),
    edit: edit(translate)
  });
}
